import { useState } from "react";
import { Search, Plus, Loader2, X, Hash, Check, SearchX } from "lucide-react";
import { toast } from "sonner";
import { searchAnime, addAnime, STATUS_LABELS } from "../lib/api";

export default function SearchDialog({ open, onClose, onAdded }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [adding, setAdding] = useState(null);
  const [addingStatus, setAddingStatus] = useState("completed");
  const [malIdMode, setMalIdMode] = useState(false);
  const [malIdInput, setMalIdInput] = useState("");
  const [upstreamDown, setUpstreamDown] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastQuery, setLastQuery] = useState("");

  if (!open) return null;

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) return;
    setLoading(true);
    setUpstreamDown(false);
    setLastQuery(query);
    try {
      const data = await searchAnime(query);
      setResults(data.results || []);
      setHasSearched(true);
      if (data.upstream_error) {
        setUpstreamDown(true);
        toast.error("Search is temporarily down. Try adding by MAL ID.");
      }
    } catch (err) {
      toast.error("Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Shared error handling: when the backend hands back a `close matches`
  // suggestions payload, slot those into the results list so the user has
  // a one-tap alternative instead of a dead-end error toast.
  const handleAddError = (err) => {
    const raw = err?.response?.data?.detail;
    if (raw && typeof raw === "object" && Array.isArray(raw.suggestions)) {
      toast.error(raw.message || "Couldn't add that one — try a close match");
      if (raw.suggestions.length > 0) {
        // Merge suggestions with existing results, deduped by mal_id.
        setResults((prev) => {
          const existing = new Set(prev.map((r) => r.mal_id));
          const extras = raw.suggestions
            .filter((s) => s.mal_id && !existing.has(s.mal_id))
            .map((s) => ({ ...s, _suggested: true }));
          return [...extras, ...prev];
        });
      }
      return;
    }
    toast.error(typeof raw === "string" ? raw : "Failed to add");
  };

  const handleAdd = async (r) => {
    if (r.already_tracked) return;
    setAdding(r.mal_id);
    try {
      await addAnime({ mal_id: r.mal_id, status: addingStatus });
      toast.success(`Added: ${r.title}`);
      // Reflect the just-added state on the row so a second click can't dup-add.
      setResults((prev) => prev.map((x) => x.mal_id === r.mal_id ? { ...x, already_tracked: true } : x));
      onAdded?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        // Already-in-catalogue race: mark it so and keep the dialog open.
        setResults((prev) => prev.map((x) => x.mal_id === r.mal_id ? { ...x, already_tracked: true } : x));
        toast.error(err?.response?.data?.detail || "Already in catalogue");
      } else {
        handleAddError(err);
      }
    } finally {
      setAdding(null);
    }
  };

  const handleAddByMalId = async (e) => {
    e?.preventDefault?.();
    const id = parseInt(malIdInput, 10);
    if (!id || id < 1) {
      toast.error("Enter a valid MAL ID (positive integer)");
      return;
    }
    setAdding(id);
    try {
      const res = await addAnime({ mal_id: id, status: addingStatus });
      toast.success(`Added: ${res.added}`);
      // Consistent with title-search: stay open so users can add multiple.
      setMalIdInput("");
      onAdded?.();
    } catch (err) {
      handleAddError(err);
    } finally {
      setAdding(null);
    }
  };

  return (
    <div
      data-testid="search-dialog"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 backdrop-blur-md p-4 md:p-10 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl border border-white/15 bg-neutral-950 shadow-[0_0_0_1px_rgba(255,255,255,0.05)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <h2 className="font-display text-3xl uppercase tracking-tight text-white">Find Anime</h2>
          <button
            data-testid="search-dialog-close"
            onClick={onClose}
            className="border border-white/15 p-1 text-neutral-400 hover:bg-white hover:text-black"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex border-b border-white/10">
          <button
            data-testid="mode-search-btn"
            onClick={() => setMalIdMode(false)}
            className={`flex-1 border-r border-white/10 py-2 font-mono text-[10px] uppercase tracking-widest ${!malIdMode ? "bg-white text-black" : "text-neutral-500 hover:text-white"}`}
          >
            <Search className="mr-2 inline h-3 w-3" /> Title Search
          </button>
          <button
            data-testid="mode-malid-btn"
            onClick={() => setMalIdMode(true)}
            className={`flex-1 py-2 font-mono text-[10px] uppercase tracking-widest ${malIdMode ? "bg-white text-black" : "text-neutral-500 hover:text-white"}`}
          >
            <Hash className="mr-2 inline h-3 w-3" /> By MAL ID
          </button>
        </div>

        {!malIdMode ? (
          <form onSubmit={handleSearch} className="flex gap-2 border-b border-white/10 px-5 py-4">
            <div className="flex flex-1 items-center gap-3 border border-white/15 bg-transparent px-3">
              <Search className="h-4 w-4 text-neutral-500" />
              <input
                data-testid="search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. Frieren, Attack on Titan, Chainsaw Man..."
                className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none"
              />
            </div>
            <select
              data-testid="search-status-select"
              value={addingStatus}
              onChange={(e) => setAddingStatus(e.target.value)}
              className="border border-white/15 bg-black px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-white focus:outline-none focus:border-[#FF3B30]"
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <button
              data-testid="search-submit"
              type="submit"
              disabled={loading}
              className="border border-white/15 bg-[#FF3B30] px-4 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-[#D62828] disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleAddByMalId} className="flex gap-2 border-b border-white/10 px-5 py-4">
            <div className="flex flex-1 items-center gap-3 border border-white/15 bg-transparent px-3">
              <Hash className="h-4 w-4 text-neutral-500" />
              <input
                data-testid="malid-input"
                autoFocus
                type="number"
                min={1}
                value={malIdInput}
                onChange={(e) => setMalIdInput(e.target.value)}
                placeholder="MAL ID (e.g. 16498 = Attack on Titan)"
                className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none"
              />
            </div>
            <select
              data-testid="malid-status-select"
              value={addingStatus}
              onChange={(e) => setAddingStatus(e.target.value)}
              className="border border-white/15 bg-black px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-white focus:outline-none focus:border-[#FF3B30]"
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <button
              data-testid="malid-submit"
              type="submit"
              disabled={adding != null}
              className="border border-white/15 bg-[#FF3B30] px-4 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-[#D62828] disabled:opacity-50"
            >
              {adding != null ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
            </button>
          </form>
        )}

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          {malIdMode ? (
            <div className="space-y-2 text-xs text-neutral-400">
              <p>Find any anime&apos;s MAL ID on its MyAnimeList URL: <span className="font-mono text-white">myanimelist.net/anime/<span className="text-[#FF3B30]">16498</span>/...</span></p>
              <p className="pt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Popular IDs to try:</p>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[11px] text-neutral-300 md:grid-cols-3">
                <li><span className="text-[#FF3B30]">52991</span> Frieren</li>
                <li><span className="text-[#FF3B30]">16498</span> Attack on Titan</li>
                <li><span className="text-[#FF3B30]">1535</span> Death Note</li>
                <li><span className="text-[#FF3B30]">44511</span> Chainsaw Man</li>
                <li><span className="text-[#FF3B30]">37521</span> Vinland Saga</li>
                <li><span className="text-[#FF3B30]">5114</span> FMA: Brotherhood</li>
                <li><span className="text-[#FF3B30]">1</span> Cowboy Bebop</li>
                <li><span className="text-[#FF3B30]">32281</span> Kimi no Na wa</li>
                <li><span className="text-[#FF3B30]">50265</span> Spy x Family</li>
              </ul>
            </div>
          ) : (
            <>
              {loading && (
                <div className="flex items-center justify-center py-10 text-neutral-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}
              {!loading && upstreamDown && (
                <div className="border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-200">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400">// upstream error</p>
                  <p className="mt-2">MyAnimeList search is having a moment. Switch to <button onClick={() => setMalIdMode(true)} className="underline">Add by MAL ID</button> to bypass it.</p>
                </div>
              )}
              {!loading && !upstreamDown && results.length === 0 && !hasSearched && (
                <p
                  data-testid="search-initial-hint"
                  className="py-10 text-center font-mono text-xs uppercase tracking-widest text-neutral-600"
                >
                  Type to search MyAnimeList
                </p>
              )}
              {!loading && !upstreamDown && results.length === 0 && hasSearched && (
                <div
                  data-testid="search-empty-state"
                  className="border border-dashed border-white/15 py-14 text-center"
                >
                  <SearchX className="mx-auto h-8 w-8 text-neutral-600" />
                  <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                    No matches for &ldquo;{lastQuery}&rdquo;
                  </p>
                  <p className="mt-2 max-w-sm mx-auto text-sm text-neutral-400">
                    Try the original Japanese/romaji title, drop punctuation, or switch to <button onClick={() => setMalIdMode(true)} className="text-[#FF3B30] underline">Add by MAL ID</button>.
                  </p>
                </div>
              )}
              <ul className="space-y-3">
                {results.map((r) => (
                  <li
                    key={r.mal_id}
                    data-testid={`search-result-${r.mal_id}`}
                    className="flex gap-4 border border-white/10 bg-neutral-950 p-3"
                  >
                    {r.image_url && (
                      <img src={r.image_url} alt="" className="h-24 w-16 flex-shrink-0 object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      {r._suggested && (
                        <span
                          data-testid={`search-suggested-${r.mal_id}`}
                          className="mb-1 inline-block border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-[#FF3B30]"
                        >
                          Did you mean?
                        </span>
                      )}
                      <h3 className="font-semibold text-white line-clamp-1">{r.title}</h3>
                      {r.title_original && r.title_original !== r.title && (
                        <p className="mt-0.5 line-clamp-1 text-[11px] text-neutral-500">{r.title_original}</p>
                      )}
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        {[r.type, r.year, r.episodes ? `${r.episodes} ep` : null, r.score ? `★ ${r.score}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <p className="mt-2 text-xs text-neutral-400 line-clamp-2">{r.synopsis}</p>
                    </div>
                    {r.already_tracked ? (
                      <div
                        data-testid={`search-tracked-${r.mal_id}`}
                        className="flex flex-shrink-0 items-center gap-1 self-start border border-emerald-500/50 bg-emerald-500/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300"
                      >
                        <Check className="h-3 w-3" /> In catalogue
                      </div>
                    ) : (
                      <button
                        data-testid={`search-add-${r.mal_id}`}
                        onClick={() => handleAdd(r)}
                        disabled={adding === r.mal_id}
                        className="self-start border border-white/15 bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-black hover:bg-[#FF3B30] hover:text-white disabled:opacity-50"
                      >
                        {adding === r.mal_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Plus className="mr-1 inline h-3 w-3" />Add</>}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
